import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Container, Typography, Grid, Box, ToggleButtonGroup, ToggleButton, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RemoveIcon from '@mui/icons-material/Remove';
import billingApi from '../services/billingApi';
import { useAuth } from '../contexts/AuthContext';
import PlanCard from '../components/PlanCard';
import PlanCardSkeleton from '../components/PlanCardSkeleton';
import DiscountCodeInput from '../components/DiscountCodeInput';
import PlanActionModal from '../components/PlanActionModal';
import { useCart } from '../contexts/CartContext';
import { services, productLabelToServiceKey } from '../proxy-config';

function getProductUrl(productId) {
  const key = productLabelToServiceKey[productId];
  return key ? services[key]?.frontend : null;
}

export default function PlansPage() {
  const { user, login, loading: authLoading } = useAuth();
  const { addItem } = useCart();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const productFilter = searchParams.get('product');
  const referralParam = searchParams.get('ref');

  const [plans, setPlans] = useState([]);
  const [showSignIn, setShowSignIn] = useState(false);
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(productFilter || '');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [discountCode, setDiscountCode] = useState('');
  const [referralCode, setReferralCode] = useState(referralParam || '');
  const [referralInfo, setReferralInfo] = useState(null);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [catalog, setCatalog] = useState({});
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [actionModal, setActionModal] = useState(null);

  useEffect(() => {
    loadPlans();
  }, [selectedProduct, user]);

  // Validate referral code from URL
  useEffect(() => {
    if (!referralParam || !user) return;
    billingApi.post('/api/discounts/validate-referral', { code: referralParam })
      .then(data => {
        if (data.success) setReferralInfo(data.referral);
      })
      .catch(() => setReferralCode(''));
  }, [referralParam, user]);

  async function loadPlans() {
    setLoading(true);
    // Rule 2 of the loading standard (one pattern per region): a stale error
    // Alert must not sit above fresh skeletons on refetch.
    setError('');
    try {
      const query = selectedProduct ? `?product=${selectedProduct}` : '';
      // Use authenticated endpoint when logged in (returns actions), public otherwise
      const endpoint = user ? `/api/plans/actions${query}` : `/api/plans${query}`;
      const data = await billingApi.get(endpoint);
      setPlans(data.plans);
      if (data.catalog) setCatalog(data.catalog);

      const prods = [...new Map(data.plans.map(p => [p.productId, { id: p.productId, name: p.product_name }])).values()];
      if (!selectedProduct) setProducts(prods);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function handleSelectPlan(plan, selectedAction) {
    if (!user) {
      setShowSignIn(true);
      return;
    }

    const action = selectedAction || plan.action;
    if (!action || action.disabled) return;

    // Purchase-type actions go through the unified checkout
    const purchaseTypes = ['subscribe', 'activate_free', 'upgrade_now', 'upgrade_at_end',
      'downgrade_at_end', 'lateral_switch', 'upgrade', 'downgrade', 'lateral'];
    if (purchaseTypes.includes(action.type) || action.endpoint?.includes('/workflows/')) {
      addItem({
        id: `${plan.id}_${action.id || action.type}`,
        planId: plan.id,
        productId: plan.productId,
        productName: plan.product_name,
        planName: plan.name,
        actionType: action.type,
        priceCents: plan.price_cents,
        intervalType: plan.interval_type,
        subscriptionId: action.payload?.input_data?.subscription_id || null,
        workflowId: null,
        workflowPayload: action.payload,
        timing: action.timing || null,
        proration: action.proration || null,
        description: action.description || null,
      });
      navigate('/checkout');
      return;
    }

    // Non-purchase actions (cancel, pause, resume, etc.) stay in the modal
    setActionModal({ plan, action });
  }

  function handleActionComplete() {
    setActionModal(null);
    loadPlans();
  }

  function handleDiscountApplied(discount, code) {
    setDiscountCode(code || '');
  }

  // Group plans by product
  const grouped = plans.reduce((acc, plan) => {
    if (!acc[plan.productId]) acc[plan.productId] = { name: plan.product_name, plans: [] };
    acc[plan.productId].plans.push(plan);
    return acc;
  }, {});

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>Choose a Plan</Typography>

      {referralInfo && (
        <Alert severity="success" sx={{ mb: 2 }} icon={false}>
          <Typography variant="body2" fontWeight={600}>
            Referral discount applied: {referralInfo.discount_type === 'percentage' ? `${referralInfo.discount_value}% off` : `$${(referralInfo.discount_value / 100).toFixed(2)} off`}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Code <code>{referralCode}</code> will be applied at checkout automatically.
          </Typography>
        </Alert>
      )}

      {products.length > 1 && !productFilter && (
        <Box sx={{ mb: 3 }}>
          <ToggleButtonGroup
            value={selectedProduct}
            exclusive
            onChange={(e, val) => setSelectedProduct(val || '')}
            size="small"
          >
            <ToggleButton value="">All Products</ToggleButton>
            {products.map(p => (
              <ToggleButton key={p.id} value={p.id}>{p.name}</ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box aria-busy="true">
          <Grid container spacing={3}>
            {[0, 1, 2].map((i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <PlanCardSkeleton />
              </Grid>
            ))}
          </Grid>
          <Box component="span" role="status" sx={{ position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' }}>
            Loading plans…
          </Box>
        </Box>
      ) : (
        Object.entries(grouped).map(([productId, { name, plans: productPlans }]) => (
          <Box key={productId} sx={{ mb: 6 }}>
            {!productFilter && <Typography variant="h5" gutterBottom>{name}</Typography>}
            <Grid container spacing={3}>
              {productPlans.map(plan => (
                <Grid item xs={12} sm={6} md={4} key={plan.id}>
                  <PlanCard
                    plan={plan}
                    action={plan.action}
                    actions={plan.actions}
                    onSelect={handleSelectPlan}
                    disabled={actionLoading}
                    catalog={catalog}
                  />
                </Grid>
              ))}
            </Grid>
            {selectedPlanId && productPlans.some(p => p.id === selectedPlanId) && (
              <DiscountCodeInput planId={selectedPlanId} onApplied={handleDiscountApplied} />
            )}
          </Box>
        ))
      )}

      {/* Comparison Grid */}
      {!loading && Object.entries(grouped).map(([productId, { name, plans: productPlans }]) => {
        const monthlyPlans = productPlans.filter(p => p.interval_type === 'month');
        if (monthlyPlans.length < 2) return null;

        const allKeys = new Map();
        for (const plan of monthlyPlans) {
          flattenObj(plan.features).forEach(([key]) => {
            if (!allKeys.has(key)) {
              const catEntry = catalog[key];
              allKeys.set(key, {
                label: catEntry?.display_name || key.split('.').pop().replace(/_/g, ' '),
                description: catEntry?.description || '',
                valueOrder: catEntry?.value_order || 0
              });
            }
          });
        }
        const sortedKeys = [...allKeys.entries()].sort((a, b) => b[1].valueOrder - a[1].valueOrder);

        return (
          <Box key={`compare-${productId}`} sx={{ mt: 4, mb: 6 }}>
            <Typography variant="h5" gutterBottom>
              {name} — Feature Comparison
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Feature</TableCell>
                    {monthlyPlans.map(p => (
                      <TableCell key={p.id} align="center" sx={{ fontWeight: 700 }}>
                        {p.name}
                        <Typography variant="caption" display="block" color="text.secondary">
                          {p.price_cents === 0 ? 'Free' : `$${(p.price_cents/100).toFixed(2)}/${p.interval_type === 'year' ? 'yr' : 'mo'}`}
                        </Typography>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedKeys.map(([key, { label, description }]) => (
                    <TableRow key={key} hover title={description}>
                      <TableCell>{label}</TableCell>
                      {monthlyPlans.map(p => {
                        const value = getNestedValue(p.features, key);
                        return (
                          <TableCell key={p.id} align="center">
                            {formatComparisonValue(value)}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        );
      })}

      {user && plans.length > 0 && plans.some(p => p.price_cents > 0) && !selectedPlanId && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Have a discount code? Select a plan first, then enter your code.
          </Typography>
        </Box>
      )}

      {/* Plan Action Modal */}
      <PlanActionModal
        open={!!actionModal}
        onClose={() => setActionModal(null)}
        plan={actionModal?.plan}
        action={actionModal?.action}
        onComplete={handleActionComplete}
      />

      {/* Sign In Dialog */}
      <Dialog open={showSignIn} onClose={() => setShowSignIn(false)}>
        <DialogTitle>Sign in required</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You need to sign in to select a plan. You'll be redirected to the login page.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSignIn(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => { setShowSignIn(false); login(); }}>
            Sign In
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

// ─── Helpers ──────────────────────────────

function flattenObj(obj, prefix = '') {
  const result = [];
  for (const [key, value] of Object.entries(obj || {})) {
    const fullKey = prefix ? `${prefix}.${key}` : key;
    if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      result.push(...flattenObj(value, fullKey));
    } else {
      result.push([fullKey, value]);
    }
  }
  return result;
}

function getNestedValue(obj, path) {
  return path.split('.').reduce((o, k) => o?.[k], obj);
}

function formatComparisonValue(value) {
  if (value === true) return <CheckCircleIcon fontSize="small" color="success" />;
  if (value === false) return <RemoveIcon fontSize="small" sx={{ color: 'text.disabled' }} />;
  if (value === -1) return <Typography variant="body2" fontWeight={600} color="success.main">Unlimited</Typography>;
  if (Array.isArray(value)) {
    if (value.includes('all')) return <Typography variant="body2" fontWeight={600} color="success.main">All</Typography>;
    return <Typography variant="body2">{value.length}</Typography>;
  }
  if (typeof value === 'number') return <Typography variant="body2" fontWeight={600}>{value}</Typography>;
  return <RemoveIcon fontSize="small" sx={{ color: 'text.disabled' }} />;
}
